import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DEFAULT_AI_MODEL_ID,
  resolveAiModelOption,
  type AiModelId,
  type AiModelOption,
} from '@oneup/shared';

/**
 * Local Alice/AI preferences.
 *
 * The selected model is a non-sensitive UI preference stored in localStorage as
 * a temporary persistence layer — there is no per-user settings backend yet.
 * It is sent to the Worker on each chat turn, where it is re-validated against
 * the shared allowlist before any OpenAI call. No secrets are stored here.
 *
 * TODO (future pass): move this to per-user settings persisted server-side, with
 * per-portal default model + internal/client portal model restrictions.
 */
interface AiSettingsState {
  modelId: AiModelId;
  setModelId: (id: AiModelId) => void;
  /**
   * Connector ids the operator has switched OFF for Alice (e.g. 'stripe',
   * 'vapi', 'gmail', 'airtable_raw'). Sent to the Worker each turn, which
   * withholds those tools. UI preference, not a security boundary (per-portal
   * policy is a later pass).
   */
  disabledConnectors: string[];
  toggleConnector: (id: string) => void;
  isConnectorEnabled: (id: string) => boolean;
}

export const useAiSettingsStore = create<AiSettingsState>()(
  persist(
    (set, get) => ({
      modelId: DEFAULT_AI_MODEL_ID,
      setModelId: (id) => set({ modelId: id }),
      disabledConnectors: [],
      toggleConnector: (id) =>
        set((state) => ({
          disabledConnectors: state.disabledConnectors.includes(id)
            ? state.disabledConnectors.filter((c) => c !== id)
            : [...state.disabledConnectors, id],
        })),
      isConnectorEnabled: (id) => !get().disabledConnectors.includes(id),
    }),
    {
      name: 'oneup.ai-settings',
      partialize: (state) => ({
        modelId: state.modelId,
        disabledConnectors: state.disabledConnectors,
      }),
    },
  ),
);

/** The resolved model option for the current preference (never undefined). */
export function useSelectedAiModel(): AiModelOption {
  const modelId = useAiSettingsStore((state) => state.modelId);
  // resolveAiModelOption tolerates stale/removed ids from earlier versions.
  return resolveAiModelOption(modelId);
}
